import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  DollarSign, 
  Plus, 
  MoreVertical,
  CheckCircle,
  Clock,
  Banknote,
  Calculator,
  Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { useSettings } from '../context/SettingsContext';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusColors = {
  draft: 'bg-gray-50 text-gray-700 border-gray-200',
  approved: 'bg-blue-50 text-blue-700 border-blue-200',
  paid: 'bg-emerald-50 text-emerald-700 border-emerald-200'
};

const Payroll = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const { currencySymbol } = useSettings();
  
  const [records, setRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');

  const statusLabels = {
    draft: t('pages.payroll.draft'),
    approved: t('pages.payroll.approved'),
    paid: t('pages.payroll.paid')
  };

  useEffect(() => {
    fetchRecords();
    fetchEmployees();
  }, []);

  const fetchRecords = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/payroll`);
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/hr/employees`);
      setEmployees(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSave = async () => {
    try {
      await axios.post(`${API_URL}/api/hr/payroll`, formData);
      toast.success(t('pages.payroll.payrollCreated'));
      setShowDialog(false);
      setFormData({});
      fetchRecords();
    } catch (err) {
      toast.error(t('pages.payroll.failedToCreate'));
    }
  };

  const handleApprove = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hr/payroll/${id}/approve`);
      toast.success(t('pages.payroll.payrollApproved'));
      fetchRecords();
    } catch (err) {
      toast.error(t('pages.payroll.failedToApprove'));
    }
  };

  const handlePay = async (id) => {
    try {
      await axios.put(`${API_URL}/api/hr/payroll/${id}/pay`);
      toast.success(t('pages.payroll.markedAsPaid'));
      fetchRecords();
    } catch (err) {
      toast.error(t('pages.payroll.failedToPay'));
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('pages.payroll.confirmDelete'))) {
      try {
        await axios.delete(`${API_URL}/api/hr/payroll/${id}`);
        toast.success(t('pages.payroll.payrollDeleted'));
        fetchRecords();
      } catch (err) {
        toast.error(err.response?.data?.detail || t('pages.payroll.failedToDelete'));
      }
    }
  };

  const openNew = () => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    setFormData({ 
      period_start: format(firstDay, 'yyyy-MM-dd'),
      period_end: format(lastDay, 'yyyy-MM-dd'),
      basic_salary: 0,
      allowances: 0,
      deductions: 0,
      tax: 0
    });
    setShowDialog(true);
  };

  const getEmployeeName = (id) => {
    const emp = employees.find(e => e.id === id);
    return emp ? `${emp.first_name} ${emp.last_name}` : '-';
  };

  const calculateNet = () => {
    const basic = parseFloat(formData.basic_salary) || 0;
    const allowances = parseFloat(formData.allowances) || 0;
    const deductions = parseFloat(formData.deductions) || 0;
    const tax = parseFloat(formData.tax) || 0;
    return basic + allowances - deductions - tax;
  };

  const filteredRecords = records.filter(r => 
    filterStatus === 'all' || r.status === filterStatus
  );

  const stats = {
    total: records.reduce((sum, r) => sum + (r.net_salary || 0), 0),
    paid: records.filter(r => r.status === 'paid').reduce((sum, r) => sum + (r.net_salary || 0), 0),
    pending: records.filter(r => r.status !== 'paid').reduce((sum, r) => sum + (r.net_salary || 0), 0),
    count: records.length
  };

  return (
    <div className={`space-y-6 ${isRTL ? 'text-right' : ''}`}>
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('pages.payroll.title')}</h1>
          <p className="text-muted-foreground">{t('pages.payroll.subtitle')}</p>
        </div>
        <Button onClick={openNew} className={`gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}><Plus className="h-4 w-4" /> {t('pages.payroll.createPayroll')}</Button>
      </div>

      <div className={`grid grid-cols-4 gap-4 ${isRTL ? 'direction-rtl' : ''}`}>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{currencySymbol} {stats.total.toLocaleString()}</div><div className="text-sm text-muted-foreground">{t('pages.payroll.totalPayroll')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-emerald-600">{currencySymbol} {stats.paid.toLocaleString()}</div><div className="text-sm text-muted-foreground">{t('pages.payroll.paid')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-amber-600">{currencySymbol} {stats.pending.toLocaleString()}</div><div className="text-sm text-muted-foreground">{t('pages.payroll.pending')}</div></CardContent></Card>
        <Card><CardContent className="pt-6"><div className="text-2xl font-bold text-blue-600">{stats.count}</div><div className="text-sm text-muted-foreground">{t('pages.payroll.records')}</div></CardContent></Card>
      </div>

      <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-40"><SelectValue placeholder={t('pages.common.filter')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('pages.employees.allStatus')}</SelectItem>
            <SelectItem value="draft">{t('pages.payroll.draft')}</SelectItem>
            <SelectItem value="approved">{t('pages.payroll.approved')}</SelectItem>
            <SelectItem value="paid">{t('pages.payroll.paid')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.payroll.employee')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.payroll.period')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.payroll.basicSalary')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.payroll.allowances')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.payroll.deductions')}</TableHead>
              <TableHead className={`${isRTL ? 'text-left' : 'text-right'}`}>{t('pages.payroll.netSalary')}</TableHead>
              <TableHead className={isRTL ? 'text-right' : ''}>{t('pages.payroll.status')}</TableHead>
              <TableHead className="w-[50px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredRecords.map((record) => (
              <TableRow key={record.id}>
                <TableCell className="font-medium">{getEmployeeName(record.employee_id)}</TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {format(new Date(record.period_start), 'MMM d')} - {format(new Date(record.period_end), 'MMM d, yyyy')}
                </TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono`}>{currencySymbol} {record.basic_salary?.toLocaleString()}</TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono text-emerald-600`}>+{currencySymbol} {record.allowances?.toLocaleString()}</TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono text-red-600`}>-{currencySymbol} {(record.deductions + record.tax)?.toLocaleString()}</TableCell>
                <TableCell className={`${isRTL ? 'text-left' : 'text-right'} font-mono font-bold`}>{currencySymbol} {record.net_salary?.toLocaleString()}</TableCell>
                <TableCell><Badge className={statusColors[record.status]}>{statusLabels[record.status] || record.status}</Badge></TableCell>
                <TableCell>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8"><MoreVertical className="h-4 w-4" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align={isRTL ? 'start' : 'end'}>
                      {record.status === 'draft' && (
                        <DropdownMenuItem onClick={() => handleApprove(record.id)}><CheckCircle className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.payroll.approve')}</DropdownMenuItem>
                      )}
                      {record.status === 'approved' && (
                        <DropdownMenuItem onClick={() => handlePay(record.id)}><Banknote className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.payroll.markPaid')}</DropdownMenuItem>
                      )}
                      {record.status !== 'paid' && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(record.id)}>
                            <Trash2 className={`h-4 w-4 ${isRTL ? 'ml-2' : 'mr-2'}`} /> {t('pages.common.delete')}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
            {filteredRecords.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="h-32 text-center">
                  <DollarSign className="h-8 w-8 mx-auto text-muted-foreground/30 mb-2" />
                  <p className="text-muted-foreground">{t('pages.payroll.noPayroll')}</p>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{t('pages.payroll.createPayroll')}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('pages.payroll.employee')} *</Label>
              <Select value={formData.employee_id || ''} onValueChange={v => setFormData({...formData, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder={t('pages.attendance.selectEmployee')} /></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.payroll.periodStart')}</Label>
                <Input type="date" value={formData.period_start || ''} onChange={e => setFormData({...formData, period_start: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.payroll.periodEnd')}</Label>
                <Input type="date" value={formData.period_end || ''} onChange={e => setFormData({...formData, period_end: e.target.value})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.payroll.basicSalary')}</Label>
                <Input type="number" value={formData.basic_salary || ''} onChange={e => setFormData({...formData, basic_salary: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.payroll.allowances')}</Label>
                <Input type="number" value={formData.allowances || ''} onChange={e => setFormData({...formData, allowances: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('pages.payroll.deductions')}</Label>
                <Input type="number" value={formData.deductions || ''} onChange={e => setFormData({...formData, deductions: parseFloat(e.target.value) || 0})} />
              </div>
              <div className="space-y-2">
                <Label>{t('pages.payroll.tax')}</Label>
                <Input type="number" value={formData.tax || ''} onChange={e => setFormData({...formData, tax: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
            <div className="bg-muted/50 rounded-lg p-4">
              <div className={`flex justify-between font-bold text-lg ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span>{t('pages.payroll.netSalary')}</span>
                <span className="font-mono">{currencySymbol} {calculateNet().toLocaleString()}</span>
              </div>
            </div>
          </div>
          <DialogFooter className={isRTL ? 'flex-row-reverse' : ''}>
            <Button variant="outline" onClick={() => setShowDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={handleSave}>{t('pages.common.add')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
